import React from 'react';
import { motion } from 'framer-motion';
import { StarIcon } from './icons';
import { useLanguage } from '../context/LanguageContext';
const wordVariants = {
  hidden: {
    opacity: 0,
    y: 20,
    filter: 'blur(4px)'
  },
  visible: (i: number) => ({
    opacity: 1,
    y: 0,
    filter: 'blur(0px)',
    transition: {
      duration: 0.4,
      delay: i * 0.06,
      ease: 'easeOut'
    }
  })
};
function AnimatedText({
  text,
  className
}: {
  text: string;
  className?: string;
}) {
  const words = text.split(' ');
  return <motion.p className={className} initial="hidden" whileInView="visible" viewport={{
    once: true,
    margin: '-30px'
  }} data-id="element-64">
      {words.map((word, i) => <motion.span key={i} custom={i} variants={wordVariants} className="inline-block mr-[0.3em]" data-id="element-65">
          {word}
        </motion.span>)}
    </motion.p>;
}
export function DreamTransformation() {
  const {
    t
  } = useLanguage();
  return <section className="py-12 bg-brand-green-dark relative overflow-hidden" data-id="element-66">
      {/* Subtle pattern overlay */}
      <div className="absolute inset-0 opacity-10" style={{
      backgroundImage: 'radial-gradient(#D4860A 1px, transparent 1px)',
      backgroundSize: '30px 30px'
    }} data-id="element-67"></div>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10" data-id="element-68">
        <motion.div initial={{
        opacity: 0
      }} whileInView={{
        opacity: 1
      }} viewport={{
        once: true,
        margin: '-100px'
      }} transition={{
        duration: 0.6
      }} data-id="element-69">
          <motion.div className="flex items-center justify-center gap-4 mb-12" initial={{
          opacity: 0,
          y: -20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.5
        }} data-id="element-70">
            <motion.div animate={{
            rotate: [0, 15, -15, 0]
          }} transition={{
            duration: 4,
            repeat: Infinity,
            ease: 'easeInOut'
          }} data-id="element-71">
              <StarIcon className="w-10 h-10 text-brand-gold" data-id="element-72" />
            </motion.div>
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-heading font-bold text-white text-center" data-id="element-73">
              {t('dream.title')}
            </h2>
          </motion.div>

          <motion.div initial={{
          opacity: 0,
          y: 30,
          rotateX: 5
        }} whileInView={{
          opacity: 1,
          y: 0,
          rotateX: 0
        }} viewport={{
          once: true,
          margin: '-50px'
        }} transition={{
          duration: 0.8,
          ease: [0.16, 1, 0.3, 1]
        }} className="bg-brand-bg-dark/80 backdrop-blur-sm p-8 sm:p-12 rounded-xl border border-brand-gold/30 shadow-2xl" data-id="element-74">
            <div className="space-y-8 text-lg sm:text-xl text-gray-200" data-id="element-75">
              <AnimatedText text={t('dream.p1')} className="leading-relaxed" data-id="element-76" />

              <motion.div initial={{
              opacity: 0,
              scale: 0.8
            }} whileInView={{
              opacity: 1,
              scale: 1
            }} viewport={{
              once: true
            }} transition={{
              duration: 0.7,
              delay: 0.3,
              type: 'spring',
              stiffness: 100
            }} className="text-2xl sm:text-3xl font-heading font-bold text-brand-gold text-center py-4" data-id="element-77">
                <span data-id="element-78">{t('dream.highlight1')}</span>
                <motion.span className="text-white underline decoration-brand-gold underline-offset-4 inline-block" animate={{
                y: [0, -4, 0]
              }} transition={{
                duration: 2.5,
                repeat: Infinity,
                ease: 'easeInOut',
                delay: 0.5
              }} data-id="element-79">
                  {t('dream.highlight2')}
                </motion.span>
              </motion.div>

              <AnimatedText text={t('dream.p2')} className="leading-relaxed" data-id="element-80" />

              <motion.div initial={{
              opacity: 0,
              width: '0%'
            }} whileInView={{
              opacity: 1,
              width: '100%'
            }} viewport={{
              once: true
            }} transition={{
              duration: 1,
              delay: 0.5,
              ease: 'easeOut'
            }} className="mt-10 pt-8 border-t border-gray-700/50 text-center overflow-hidden" data-id="element-81">
                <motion.p initial={{
                opacity: 0,
                y: 10
              }} whileInView={{
                opacity: 1,
                y: 0
              }} viewport={{
                once: true
              }} transition={{
                duration: 0.6,
                delay: 1
              }} className="text-xl sm:text-2xl font-medium text-white" data-id="element-82">
                  {t('dream.p3')}
                  <strong className="text-brand-gold font-heading tracking-wide" data-id="element-83">
                    {t('dream.p3_strong')}
                  </strong>{' '}
                  {t('dream.p3_end')}
                </motion.p>
              </motion.div>
            </div>
          </motion.div>
        </motion.div>
      </div>
    </section>;
}